import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * <low-stock-message>
 *
 * Renders the automatic low-stock text and keeps it in sync with the
 * currently selected variant.
 *
 * On the product page, variant changes dispatch `ThemeEvents.variantUpdate` on
 * the closest `.shopify-section` (or `dialog`, inside quick add/quick view).
 * Inside a product card, `product-card.js` stops that event from bubbling past
 * the `<product-card>` element itself, so this component listens on whichever
 * ancestor is closest: the product card, the section, or a dialog.
 */
class LowStockMessage extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('product-card, .shopify-section, dialog');
    this.scope?.addEventListener(ThemeEvents.variantUpdate, this.updateLowStockMessage);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(ThemeEvents.variantUpdate, this.updateLowStockMessage);
  }

  /**
   * @param {CustomEvent} event
   */
  updateLowStockMessage = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      // A variant update for a different product on the same page (e.g. product
      // recommendations, quick add) — ignore it.
      return;
    }

    const newHtml = event.detail?.data?.html;
    if (!newHtml) return;

    const newLowStockMessage = newHtml.querySelector(`low-stock-message[data-product-id="${this.dataset.productId}"]`)
      ?? newHtml.querySelector('low-stock-message');

    if (!newLowStockMessage) return;

    // childrenOnly: false — the `hidden` attribute (and class) live on this
    // element itself, not just its children, so the whole node needs to morph.
    morph(this, newLowStockMessage, { childrenOnly: false });
  };
}

if (!customElements.get('low-stock-message')) {
  customElements.define('low-stock-message', LowStockMessage);
}